import { t } from 'i18n';
import { PromoBlock, PromoBlockStatus } from '@teko/ecommerce';
import { CartLine, CartSeller } from '@teko/ui-cart/desktop';
import { Card, SvgIcon, styled } from '@teko/ui';
import React, { useState } from 'react';
import { CartContextState, useTenantConfig } from 'contexts';
import { convertCartLineProduct } from 'helpers/cart';
import { CartOrderLine, CartSellerLine } from 'types/cart';
import { DetailComboModal } from './Combo';
import { useCartCombo } from '../../../hooks';
import {
  getBenefitsList,
  getComboLineItems,
  getComboPromotionId,
  getMaxItemLineQuantity,
} from '../../../helpers';
interface CartLineListProps {
  orderLines?: CartOrderLine[];
  sellerLines?: CartSellerLine[];
  onEditPrice?: (
    id: string,
    price: number,
    defaultExpectedPrice: number,
    maxConsultationServiceFee?: number,
  ) => void;
  cart: CartContextState;
  orderLineMaximumQuantity?: number;
  isAllowEditPrice?: boolean;
}
interface ComboBlockProps {
  cart: CartContextState;
  onShowModal: () => void;
  onEditPrice?: (
    id: string,
    price: number,
    defaultExpectedPrice: number,
    maxConsultationServiceFee?: number,
  ) => void;
  orderLineMaximumQuantity?: number;
  isAllowEditPrice?: boolean;
}
const VariantEditor = styled.div`
  color: #1990ff;
  line-height: 20px;
  font-size: 13px;
  display: flex;
  align-items: center;
  padding-left: 2.9rem;
  align-content: center;
  padding-bottom: 16px;
  cursor: pointer;
`;
const StyledSvgIcon = styled(SvgIcon)`
  margin-left: 9px;
`;
const PromoBlockWrapper = styled.div`
  padding: 0 1rem 0 2.9rem;
`;
const ComboBlock: React.FC<ComboBlockProps> = ({
  cart,
  onShowModal,
  onEditPrice,
  orderLineMaximumQuantity = 999,
  isAllowEditPrice = false,
}: ComboBlockProps) => {
  const { uomCodeToDisplayDecimalQuantity } = useTenantConfig();
  const isChecked = cart.comboProducts.every((el) => el.isSelected);
  const indeterminate =
    !isChecked && cart.comboProducts.some((el) => el.isSelected);
  const benefitList = getBenefitsList(
    cart.comboBenefits,
    cart?.giftsImgMap,
    false,
  );
  return (
    <Card marginBottom="1rem">
      <CartSeller
        imageUrl={''}
        name={t('Combo sản phẩm')}
        grandTotal={cart.comboGrandTotal}
        checked={isChecked}
        indeterminate={indeterminate}
        onCheck={() => {
          cart.onSelectCombo();
        }}
        isAvatarHidden={true}
      />
      {cart.comboProducts.map((product: CartOrderLine) => {
        return (
          <CartLine
            marginBottom="s16"
            key={product.id}
            showCheck
            onChangeQuantity={(newQuantity: number): void => {
              cart.onChangeQuantity(product, newQuantity);
            }}
            onRemove={(): void => {
              cart.onRemoveItem(product);
            }}
            onEditPrice={() => {
              onEditPrice(
                product.id,
                product.unitPrice - product.unitDiscount,
                product.price,
                product.maxConsultationServiceFee || 0,
              );
            }}
            disableProductPromo={cart.order.disableProductPromo}
            product={convertCartLineProduct(product, cart.order.promotionsMap)}
            onCheck={() => cart.onSelectOrderLine(product)}
            maximumValue={getMaxItemLineQuantity(
              product,
              orderLineMaximumQuantity,
            )}
            isAllowEditPrice={isAllowEditPrice}
            isQuantityDecimal={
              product.product.productInfo.uomCode ===
              uomCodeToDisplayDecimalQuantity
            }
          />
        );
      })}
      {benefitList.length > 0 && (
        <PromoBlockWrapper>
          <PromoBlock
            status={PromoBlockStatus.ACTIVE}
            hasBorderRadius={true}
            benefits={benefitList}
            showRight={false}
            blockBackground={'#F8F8FC'}
          />
        </PromoBlockWrapper>
      )}
      <VariantEditor onClick={onShowModal}>
        <span>{t('Tùy chọn sản phẩm trong combo')}</span>
        <StyledSvgIcon name={'chevron-forward'} color={'#1990FF'} size={13} />
      </VariantEditor>
    </Card>
  );
};
export const CartLineList: React.FC<CartLineListProps> = ({
  orderLines = [],
  sellerLines = [],
  onEditPrice,
  cart,
  orderLineMaximumQuantity = 999,
  isAllowEditPrice = false,
}: CartLineListProps) => {
  const comboSku = cart.comboProducts?.[0]?.sku || '';
  const { onAddComboToCart } = useCartCombo(comboSku);
  const { uomCodeToDisplayDecimalQuantity } = useTenantConfig();
  const [showComboModal, setShowComboModal] = useState(false);

  if (sellerLines.length) {
    return (
      <>
        {sellerLines
          .filter((cartSeller) => cartSeller.orderLines.length)
          .map((cartSeller) => {
            const checked = cartSeller.orderLines.every((el) => el.isSelected);
            const indeterminate =
              !checked && cartSeller.orderLines.some((el) => el.isSelected);
            return (
              <Card key={cartSeller?.seller?.id} marginBottom="1rem">
                <CartSeller
                  name={cartSeller?.seller?.displayName}
                  imageUrl={cartSeller?.seller.logo}
                  grandTotal={cartSeller.grandTotal}
                  checked={checked}
                  indeterminate={indeterminate}
                  onCheck={(checked: boolean) =>
                    cart.onSelectSeller(cartSeller?.seller?.id, checked)
                  }
                />
                {cartSeller.orderLines.map((product: CartOrderLine) => {
                  return (
                    <CartLine
                      marginBottom="s16"
                      key={product.id}
                      showCheck
                      onChangeQuantity={(newQuantity: number): void => {
                        cart.onChangeQuantity(product, newQuantity);
                      }}
                      onRemove={(): void => {
                        cart.onRemoveItem(product);
                      }}
                      onEditPrice={() =>
                        onEditPrice(
                          product.id,
                          product.unitPrice - product.unitDiscount,
                          product.price,
                          product.maxConsultationServiceFee || 0,
                        )
                      }
                      disableProductPromo={cart.order.disableProductPromo}
                      product={convertCartLineProduct(
                        product,
                        cart.order.promotionsMap,
                      )}
                      onCheck={() => cart.onSelectOrderLine(product)}
                      maximumValue={getMaxItemLineQuantity(
                        product,
                        orderLineMaximumQuantity,
                      )}
                      isAllowEditPrice={isAllowEditPrice}
                      isQuantityDecimal={
                        product.product.productInfo.uomCode ===
                        uomCodeToDisplayDecimalQuantity
                      }
                    />
                  );
                })}
              </Card>
            );
          })}
        {!!cart.comboProducts.length && (
          <>
            <ComboBlock
              cart={cart}
              onShowModal={() => {
                setShowComboModal(true);
              }}
              onEditPrice={onEditPrice}
              orderLineMaximumQuantity={orderLineMaximumQuantity}
              isAllowEditPrice={isAllowEditPrice}
            />
            <DetailComboModal
              type={'dynamic'}
              onCloseModal={() => {
                setShowComboModal(false);
              }}
              visible={showComboModal}
              onAddToCart={async (type, payload) => {
                await onAddComboToCart(type, payload, true);
                setShowComboModal(false);
              }}
              promotionId={getComboPromotionId(cart)}
              comboLineItems={getComboLineItems(cart.comboProducts)}
            />
          </>
        )}
      </>
    );
  }
  if (orderLines.length) {
    return (
      <>
        {orderLines.map((orderLine) => {
          return (
            <CartLine
              marginBottom="s16"
              key={orderLine.id}
              showCheck
              onChangeQuantity={(newQuantity: number): void => {
                cart.onChangeQuantity(orderLine, newQuantity);
              }}
              onRemove={(): void => {
                cart.onRemoveItem(orderLine);
              }}
              disableProductPromo={cart.order.disableProductPromo}
              product={convertCartLineProduct(
                orderLine,
                cart.order.promotionsMap,
              )}
              onCheck={() => cart.onSelectOrderLine(orderLine)}
              maximumValue={getMaxItemLineQuantity(
                orderLine,
                orderLineMaximumQuantity,
              )}
              isQuantityDecimal={
                orderLine.product.productInfo.uomCode ===
                uomCodeToDisplayDecimalQuantity
              }
            />
          );
        })}
      </>
    );
  }
  return null;
};
